import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import l from '../../../lang';
import './styles.scss';
import Loading from '../../common/loading';
import Input from '../../common/input';
import { Button } from '../../common/button';
import { getMerchantMarketingConfig, updateMerchantMarketingConfig } from '../../../store/marketing/actions';
import TopDashboardSection from '../../common/top-dashboard-section';
import AlertMessages from '../alert-messages';
import Card from '../../common/card';
import CardDescription from '../../common/card-description';
import CollapsibleCard from '../../common/collapsible-card';
import { ReactComponent as MetaPixelIcon } from '../../../assets/icons/marketing/meta-pixel.svg';
import { ReactComponent as GoogleAnalyticsIcon } from '../../../assets/icons/marketing/google-analytics.svg';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PanelStore } from '../../../store/interfaces';
import { MerchantMarketingConfig } from '../../../store/marketing/interfaces';
import MessageError from '../../common/message-error';
import { ErrorInfo } from '../../../utils/error-codes';
import SuccessfulAnimation from '../../common/successful-animation';
import DialogConfirm from '../../common/confirm/dialog-confirm';
import CardConfirm from '../../common/confirm/card-confirm';

const Marketing = ({
  loading,
  requestSuccess,
  fnGetMarketingConfig,
  fnUpdateMarketingConfig,
  error,
  errorInfo,
  merchantMarketingConfig,
}: {
  loading: boolean;
  requestSuccess: boolean;
  error: boolean;
  errorInfo: ErrorInfo;
  merchantMarketingConfig: MerchantMarketingConfig | null;
  fnGetMarketingConfig: () => void;
  fnUpdateMarketingConfig: (payload: MerchantMarketingConfig) => void;
}) => {
  const [isPixelLoading, setIsPixelLoading] = useState(false);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);

  const [facebookPixelId, setFacebookPixelId] = useState('');
  const [facebookPixelAccessToken, setFacebookPixelAccessToken] = useState('');
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState('');

  useEffect(() => {
    if (requestSuccess) {
      setShowSuccess(true);
    }
  }, [requestSuccess]);

  useEffect(() => {
    if (!loading) {
      setIsPixelLoading(false);
      setIsAnalyticsLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    fnGetMarketingConfig();
  }, []);

  useEffect(() => {
    if (merchantMarketingConfig) {
      setFacebookPixelId(merchantMarketingConfig.facebook_pixel_id || '');
      setGoogleAnalyticsId(merchantMarketingConfig.google_analytics_id || '');
      setFacebookPixelAccessToken(merchantMarketingConfig.pixel_access_token || '');
    }
  }, [merchantMarketingConfig]);

  const handleSavePixel = (pixelId: string) => {
    setIsPixelLoading(true);
    fnUpdateMarketingConfig({ facebook_pixel_id: pixelId, pixel_access_token: facebookPixelAccessToken });
  };

  const handleSaveAnalytics = (analyticsId: string) => {
    setIsAnalyticsLoading(true);
    fnUpdateMarketingConfig({ google_analytics_id: analyticsId });
  };

  const onClose = () => {
    setShowSuccess(false);
  };

  return (
    <>
      <TopDashboardSection title={l('menu.item.MARKETING.title')} subtitle={l('menu.item.MARKETING.subtitle')} />
      <AlertMessages />
      {showSuccess && (
        <DialogConfirm onClose={onClose}>
          <CardConfirm
            onClose={onClose}
            headline={l('marketing.success.headline')}
            description={''}
            confirmChildren={l('marketing.success.button')}
            level={'checked'}
          />
        </DialogConfirm>
      )}
      <Card>
        <CardDescription>{l('menu.item.MARKETING.desc')}</CardDescription>
        <Card>
          <div>
            {error && (
              <div className="create-theme__error">
                <MessageError errorInfo={errorInfo} />
              </div>
            )}
            <Box mb={'20px'}>
              <CollapsibleCard title={l('marketing.pixel.title')} defaultExpanded={true} icon={<MetaPixelIcon />}>
                <div className="mkt_collapsible_card_body">
                  <p>{l('marketing.pixel.subtitle')}</p>
                  <Input
                    name="pixelId"
                    onFocus={() => {}}
                    label={l('marketing.pixel.input.title')}
                    placeholder={l('marketing.pixel.input.placeholder')}
                    onChange={e => setFacebookPixelId(e.target.value)}
                    value={facebookPixelId}
                  />
                  <Input
                    name="pixelToken"
                    onFocus={() => {}}
                    label={l('marketing.pixelToken.input.title')}
                    placeholder={l('marketing.pixelToken.input.placeholder')}
                    onChange={e => setFacebookPixelAccessToken(e.target.value)}
                    value={facebookPixelAccessToken}
                  />
                  <Box mt={'16px'} display="flex" alignItems="center" gap={'8px'} color="primary.main">
                    <a href={l('marketing.pixel.configHelp.link')}>{l('marketing.pixel.configHelp.title')}</a>
                    <OpenInNewIcon />
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="button"
                      size="large"
                      variant="contained"
                      sx={{ marginTop: '24px' }}
                      disabled={isPixelLoading || facebookPixelId.trim() === ''}
                      onClick={() => handleSavePixel(facebookPixelId)}
                    >
                      {isPixelLoading ? '...' : l('common.save')}
                    </Button>
                  </Box>
                </div>
              </CollapsibleCard>
            </Box>
            <CollapsibleCard title={l('marketing.analytics.title')} defaultExpanded={true} icon={<GoogleAnalyticsIcon />}>
              <div className="mkt_collapsible_card_body">
                <p>{l('marketing.analytics.subtitle')}</p>
                <Input
                  name="googleAnalyticsId"
                  onFocus={() => {}}
                  label={l('marketing.analytics.input.title')}
                  placeholder={l('marketing.analytics.input.placeholder')}
                  onChange={e => setGoogleAnalyticsId(e.target.value)}
                  value={googleAnalyticsId}
                />
                <Box mt={'16px'} display="flex" alignItems="center" gap={'8px'} color="primary.main">
                  <a href={l('marketing.analytics.configHelp.link')}>{l('marketing.analytics.configHelp.title')}</a>
                  <OpenInNewIcon />
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    type="button"
                    size="large"
                    variant="contained"
                    sx={{ marginTop: '24px' }}
                    disabled={isAnalyticsLoading || googleAnalyticsId.trim() === ''}
                    onClick={() => handleSaveAnalytics(googleAnalyticsId)}
                  >
                    {isAnalyticsLoading ? '...' : l('common.save')}
                  </Button>
                </Box>
              </div>
            </CollapsibleCard>
          </div>
        </Card>
      </Card>
    </>
  );
};

const mapStateToProps = (state: PanelStore.RootState) => ({
  loading: state.marketingConfig.loading,
  error: state.marketingConfig.error,
  errorInfo: state.marketingConfig.errorInfo,
  merchantMarketingConfig: state.marketingConfig.merchantMarketingConfig,
  requestSuccess: state.marketingConfig.requestSuccess,
});

const mapDispatchToProps = (dispatch: any) => ({
  fnGetMarketingConfig: () => dispatch(getMerchantMarketingConfig()),
  fnUpdateMarketingConfig: (payload: any) => dispatch(updateMerchantMarketingConfig(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Marketing);
